import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert, Divider } from 'antd';
import { MailOutlined, UserOutlined, ApartmentOutlined } from '@ant-design/icons';
import { superadminApi } from '../../services/superadminApi';
import { authApi } from '../../services/authApi';
import useStore from '../../store/useStore';
import { useNavigate, Link } from 'react-router-dom';

const { Title, Text } = Typography;

const Register = () => {
  const [form] = Form.useForm();
  const { setUser } = useStore();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFinish = async (values) => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const slug = values.companyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '')
        .slice(0, 50);
      // 1) Create user in backend auth
      const user = await authApi.register(values.adminEmail, values.adminName, values.password);
      setUser(user);

      // 2) Register company in superadmin
      const payload = {
        name: values.companyName,
        slug,
        ownerEmail: values.adminEmail,
        ownerName: values.adminName,
        plan: 'free',
      };
      let created = null;
      try {
        created = await superadminApi.registerCompany(payload);
        setResult(created);
      } catch (err) {
        // Non-blocking: proceed to app even if superadmin failed, but show error
        setError(`Registered user, but company registration failed: ${err.message}`);
      }
      form.resetFields();

      // 3) Navigate to dashboard with user in store
      navigate('/dashboard', { replace: true });
    } catch (e) {
      // Handle specific error messages from backend
      let errorMessage = 'Registration failed';
      
      if (e?.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="w-full max-w-4xl">
        <Card className="shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Brand / Marketing panel */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-sky-500 to-indigo-600 p-6 text-white">
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center font-bold">IF</div>
                  <span className="text-lg font-semibold tracking-wide">QQINV</span>
                </div>
                <Title level={3} className="!text-white !mb-2">Create your company</Title>
                <Text className="!text-white/80">Set up your organization and start managing inventory in minutes.</Text>
              </div>
              <ul className="mt-8 space-y-2 text-sm text-white/90">
                <li>• Multi-tenant isolation</li>
                <li>• Admin user with secure authentication</li>
                <li>• Company approval via Superadmin</li>
              </ul>
            </div>

            {/* Form panel */}
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <Title level={3} className="!mb-1 dark:!text-slate-100">Register</Title>
                <Text className="dark:!text-slate-400">Tell us about your organization and admin</Text>
              </div>

              {error && <Alert type="error" message={error} className="mb-4" />}
              {result && <Alert type="success" message={`Registered ${result?.name} (${result?.slug}). Awaiting approval.`} className="mb-4" />}

              <Form layout="vertical" form={form} onFinish={handleFinish}>
                <Form.Item name="companyName" label="Company Name" rules={[{ required: true, message: 'Company name is required' }]}> 
                  <Input size="large" prefix={<ApartmentOutlined />} placeholder="Acme Inc" />
                </Form.Item>
                <Form.Item name="adminEmail" label="Admin Email" rules={[{ required: true, message: 'Email is required' }, { type: 'email' }]}>
                  <Input size="large" prefix={<MailOutlined />} placeholder="admin@acme.com" />
                </Form.Item>
                <Form.Item name="adminName" label="Admin Name" rules={[{ required: true, message: 'Name is required' }]}>
                  <Input size="large" prefix={<UserOutlined />} placeholder="Jane Doe" />
                </Form.Item>
                <Form.Item name="password" label="Password" rules={[{ required: true, min: 6, message: 'Minimum 6 characters' }] } hasFeedback>
                  <Input.Password size="large" placeholder="Create a password" autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="confirm" label="Confirm Password" dependencies={["password"]} hasFeedback rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    }
                  })
                ]}>
                  <Input.Password size="large" placeholder="Confirm password" autoComplete="new-password" />
                </Form.Item>
                <Button type="primary" htmlType="submit" block size="large" loading={submitting}>Create account</Button>
              </Form>

              <Divider plain className="!my-6">Already registered?</Divider>
              <div className="text-center">
                <Text className="dark:!text-slate-400"> <Link to="/login">Sign in</Link></Text>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Register;
